import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, CircularProgress, Divider, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { ConversationFilterParams, Participant, UserRole } from '../../types';
import { useGetActiveDealersByManufacturerIdQuery } from '../dealer/dealer-api-slice';
import { selectCurrentUser } from '../user';
import {
  AvailableParticipantsList,
  ComposeMessageDialog,
  ConversationList,
  ConversationView,
  MessagingFilters,
  ParticipantInfoDialog,
} from './components';
import { SearchBar } from './components/search-bar';
import {
  useCreateOrGetConversationMutation,
  useGetConversationsQuery,
  useSearchAvailableParticipantsQuery,
  useSearchConversationsQuery,
  useSearchConversationsWithFiltersMutation,
} from './messaging-api-slice';

export const MessagingInboxPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationIdParam = searchParams.get('conversationId');
  const selectedConversationId = conversationIdParam ? parseInt(conversationIdParam, 10) : null;

  const currentUser = useAppSelector(selectCurrentUser);
  const isDealer = currentUser?.role === UserRole.DEALER;
  const isManufacturer =
    currentUser?.role === UserRole.SUPERVISOR || currentUser?.role === UserRole.CSR;

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filters, setFilters] = useState<ConversationFilterParams>({});
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [createOrGetConversation] = useCreateOrGetConversationMutation();
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);

  const {
    data: conversations = [],
    isLoading: isLoadingConversations,
    refetch: refetchConversations,
  } = useGetConversationsQuery(undefined, {
    skip:
      searchTerm.length > 0 ||
      Object.keys(filters).some(
        key => filters[key as keyof ConversationFilterParams] !== undefined
      ),
  });

  // Search conversations by message content when there's a search term but no filters
  const hasActiveFilters = Boolean(
    filters.dateFrom || filters.dateTo || filters.supervisorId || filters.dealerId
  );
  const {
    data: searchedConversations = [],
    isLoading: isLoadingSearchConversations,
    refetch: refetchSearchedConversations,
  } = useSearchConversationsQuery(searchTerm, {
    skip: searchTerm.length === 0 || hasActiveFilters,
  });

  const [searchWithFilters, { data: filteredResults = [], isLoading: isLoadingFilters }] =
    useSearchConversationsWithFiltersMutation();

  // Get supervisors for dealers - extract from existing conversations instead of calling user management API
  // Dealers don't have access to user management endpoint, so we get supervisors from conversations they already have
  const supervisors = useMemo(() => {
    if (!isDealer) return [];

    // Extract unique supervisors/CSRs from existing conversations
    const supervisorMap = new Map<number, string>();

    // Check all conversation sources
    const allConversations = [...conversations, ...searchedConversations, ...filteredResults];

    allConversations.forEach(conv => {
      // The otherParticipantId in conversations should be the supervisor/CSR for dealers
      // We'll use the otherParticipantName which should already be populated
      if (conv.otherParticipantId && conv.otherParticipantName) {
        supervisorMap.set(conv.otherParticipantId, conv.otherParticipantName);
      }
    });

    return Array.from(supervisorMap.entries()).map(([id, name]) => ({
      id,
      name,
    }));
  }, [isDealer, conversations, searchedConversations, filteredResults]);

  // Get dealers for manufacturers
  const { data: dealers = [] } = useGetActiveDealersByManufacturerIdQuery(
    currentUser?.manufacturerId ?? 0,
    {
      skip: !isManufacturer || !currentUser?.manufacturerId,
    }
  );

  const dealerOptions = useMemo(() => {
    return dealers.map(d => ({
      id: d.id,
      name: d.name,
    }));
  }, [dealers]);

  // Search for available participants when searching (for starting new conversations)
  // Dealers cannot search for new participants
  // Allow participant search even when filters are active (except dealer filter which is passed as parameter)
  const { data: availableParticipants = [], isLoading: isLoadingParticipants } =
    useSearchAvailableParticipantsQuery(
      {
        searchTerm,
        dealerId: filters.dealerId,
      },
      {
        skip: isDealer || searchTerm.length === 0,
      }
    );

  // Apply filters when they change
  useEffect(() => {
    const hasFilters =
      filters.dateFrom ||
      filters.dateTo ||
      filters.supervisorId ||
      filters.dealerId ||
      (searchTerm.length > 0 &&
        (filters.dateFrom || filters.dateTo || filters.supervisorId || filters.dealerId));

    if (hasFilters) {
      searchWithFilters({
        searchTerm: searchTerm || undefined,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        supervisorId: filters.supervisorId,
        dealerId: filters.dealerId,
      });
    }
  }, [searchTerm, filters, searchWithFilters]);

  // Determine what to display: existing conversations, searched conversations, or filtered results
  const displayedConversations = hasActiveFilters
    ? filteredResults
    : searchTerm.length > 0
      ? searchedConversations
      : conversations;

  // Show available participants when searching, even if filters are active
  // Only show if no conversations matched the search (after loading completes)
  // Dealers cannot see available participants (they can't start new conversations)
  const showAvailableParticipants =
    !isDealer &&
    searchTerm.length > 0 &&
    !isLoadingParticipants &&
    (hasActiveFilters
      ? !isLoadingFilters && filteredResults.length === 0
      : !isLoadingSearchConversations && searchedConversations.length === 0);

  const isLoading =
    isLoadingConversations ||
    isLoadingFilters ||
    isLoadingSearchConversations ||
    isLoadingParticipants;

  const handleConversationSelect = (id: number) => {
    setSearchParams({ conversationId: id.toString() });
  };

  const handleRefresh = () => {
    refetchConversations();
    if (hasActiveFilters) {
      searchWithFilters({
        searchTerm: searchTerm || undefined,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        supervisorId: filters.supervisorId,
        dealerId: filters.dealerId,
      });
    } else if (searchTerm.length > 0) {
      refetchSearchedConversations();
    }
  };

  const handleStartConversation = async (participantId: number) => {
    try {
      const conversation = await createOrGetConversation(participantId).unwrap();
      setSearchParams({ conversationId: conversation.id.toString() });
      setSearchTerm(''); // Clear search after starting conversation
      refetchConversations();
    } catch (error) {
      console.error('Error starting conversation:', error);
      throw error;
    }
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    if (
      term.length === 0 &&
      !Object.keys(filters).some(
        key => filters[key as keyof ConversationFilterParams] !== undefined
      )
    ) {
      setSearchParams({});
    }
  };

  const handleFiltersChange = (newFilters: ConversationFilterParams) => {
    setFilters(newFilters);
  };

  const searchPlaceholder = 'Search conversations and messages...';

  return (
    <Box
      sx={{
        height: 'calc(100vh - 180px)', // Fixed height: viewport minus header/footer
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 180px)',
      }}
    >
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Typography variant="h5" component="h1">
              Messages
            </Typography>
          </Grid>
          <Grid item>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              variant="outlined"
              disabled={isLoading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid
        container
        spacing={2}
        sx={{ flex: 1, minHeight: 0, overflow: 'hidden', height: '100%' }}
      >
        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
        >
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2, flexShrink: 0 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setComposeDialogOpen(true)}
                sx={{
                  mb: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '1rem',
                }}
              >
                Compose New Message
              </Button>
              <SearchBar
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
              />
            </Box>
            <Divider sx={{ flexShrink: 0 }} />
            <Box sx={{ flexShrink: 0 }}>
              <MessagingFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                userRole={currentUser?.role || UserRole.DEALER}
                supervisors={supervisors}
                dealers={dealerOptions}
              />
            </Box>
            <Divider sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : showAvailableParticipants ? (
                <>
                  {displayedConversations.length > 0 && (
                    <>
                      <Box sx={{ p: 2, pb: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Existing Conversations
                        </Typography>
                      </Box>
                      <ConversationList
                        conversations={displayedConversations}
                        selectedId={selectedConversationId}
                        onSelect={handleConversationSelect}
                      />
                      <Divider sx={{ my: 1 }} />
                    </>
                  )}
                  <Box sx={{ p: 2, pb: 1, pt: displayedConversations.length > 0 ? 2 : 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {displayedConversations.length > 0
                        ? 'Available Participants'
                        : 'Search Results'}
                    </Typography>
                  </Box>
                  {availableParticipants.length > 0 ? (
                    <AvailableParticipantsList
                      participants={availableParticipants}
                      onSelect={handleConversationSelect}
                      onStartConversation={handleStartConversation}
                    />
                  ) : (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No participants or conversations found matching "{searchTerm}"
                      </Typography>
                    </Box>
                  )}
                </>
              ) : displayedConversations.length === 0 && searchTerm.length > 0 && isDealer ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No conversations found matching "{searchTerm}"
                  </Typography>
                </Box>
              ) : (
                <ConversationList
                  conversations={displayedConversations}
                  selectedId={selectedConversationId}
                  onSelect={handleConversationSelect}
                />
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={8}
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
        >
          <Paper
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            {selectedConversationId ? (
              <ConversationView
                conversationId={selectedConversationId}
                onMessageSent={() => {
                  refetchConversations();
                }}
                onInfoClick={participant => {
                  setSelectedParticipant(participant);
                  setInfoDialogOpen(true);
                }}
                key={selectedConversationId}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  p: 3,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Select a conversation to view messages
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <ParticipantInfoDialog
        open={infoDialogOpen}
        onClose={() => {
          setInfoDialogOpen(false);
          setSelectedParticipant(null);
        }}
        participant={selectedParticipant}
      />

      <ComposeMessageDialog
        open={composeDialogOpen}
        onClose={() => setComposeDialogOpen(false)}
        onSelectConversation={handleConversationSelect}
        onStartConversation={handleStartConversation}
      />
    </Box>
  );
};
